import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IndicatorPeriodDto, IndicatorValueDto } from "@/lib/types/api";

interface PeriodSelectorProps {
  periods: IndicatorPeriodDto[];
  selectedPeriodId: string;
  onPeriodChange: (periodId: string) => void;
  existingValues?: IndicatorValueDto[];
  selectedIndicatorId?: string;
  label?: string;
  showLegend?: boolean;
}

export default function PeriodSelector({
  periods,
  selectedPeriodId,
  onPeriodChange,
  existingValues = [],
  selectedIndicatorId,
  label = "Select Period *",
  showLegend = true,
}: PeriodSelectorProps) {
  const [scrollIndex, setScrollIndex] = useState(0);
  const scrollAmount = 1.25; // Scroll 1.25 chips at a time

  const handlePrevious = () => {
    setScrollIndex(prev => Math.max(0, prev - scrollAmount));
  };

  const handleNext = () => {
    setScrollIndex(prev => Math.min(Math.max(0, periods.length - 3), prev + scrollAmount));
  };

  if (periods.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
        No periods available
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">{label}</Label>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {Math.floor(scrollIndex) + 1}-{Math.min(Math.floor(scrollIndex) + 3, periods.length)} of {periods.length}
        </span>
      </div>
      
      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={scrollIndex === 0}
            className="h-8 w-8 p-0 flex-shrink-0 rounded-full"
            title="Previous periods"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex-1 overflow-hidden">
            <div 
              className="flex gap-2 transition-transform duration-300 ease-out"
              style={{ 
                transform: `translateX(calc(-${scrollIndex} * (100% / 3 + 0.667rem)))`
              }}
            >
              {periods.map((period) => {
              const hasData = existingValues.some(
                v => v.indicatorId === selectedIndicatorId && v.periodId === period.id
              );
              const isSelected = selectedPeriodId === period.id;
              
              return (
                <button
                  key={period.id}
                  type="button"
                  onClick={() => onPeriodChange(period.id)}
                  className={`flex-shrink-0 px-3 py-2 rounded-lg font-semibold text-xs transition-all duration-200 relative ${
                    hasData 
                      ? 'bg-emerald-500/90 hover:bg-emerald-600/90 text-white' 
                      : 'bg-rose-500/90 hover:bg-rose-600/90 text-white'
                  } ${
                    isSelected 
                      ? 'border-[3px] border-blue-600 dark:border-blue-400 shadow-lg shadow-blue-500/50' 
                      : 'border-[3px] border-transparent shadow-md hover:shadow-lg'
                  }`}
                  style={{ minWidth: 'calc((100% - 16px) / 3)' }}
                >
                  {isSelected && (
                    <div className="absolute top-0.5 right-0.5">
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                    </div>
                  )}
                  <div className="whitespace-nowrap font-bold">
                    {period.periodLabel}
                  </div>
                  <div className="text-[10px] mt-0.5 opacity-90 font-normal">
                    {new Date(period.periodStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(period.periodEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                  </div>
                  <div className="text-[9px] mt-0.5 opacity-85">
                    {hasData ? '✓ Filled' : '○ Empty'}
                  </div>
                </button>
              );
            })}
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={scrollIndex >= Math.max(0, periods.length - 3)}
            className="h-8 w-8 p-0 flex-shrink-0 rounded-full"
            title="Next periods"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        {showLegend && (
          <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-1 text-[10px]">
              <div className="w-2.5 h-2.5 rounded bg-emerald-500/90"></div>
              <span className="text-gray-600 dark:text-gray-400">Filled</span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
              <div className="w-2.5 h-2.5 rounded bg-rose-500/90"></div>
              <span className="text-gray-600 dark:text-gray-400">Empty</span>
            </div>
            <div className="flex items-center gap-1 text-[10px]">
              <div className="w-2.5 h-2.5 rounded-full ring-2 ring-blue-500"></div>
              <span className="text-gray-600 dark:text-gray-400">Selected</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
